import { normalizeHex, PROJECT_PALETTE } from '@shared/palette';
import { Check } from 'lucide-react';
import { useRef, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';

interface SwatchPickerProps {
  value: string;
  onChange: (hex: string) => void;
  /** Colours used by OTHER active projects: marked with a dot, still selectable. */
  usedColors?: readonly string[];
  label?: string;
}

const DARK_CHECK = '#1E1E1E';
const LIGHT_CHECK = '#FFFFFF';

function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** White or #1E1E1E, whichever contrasts more with the swatch. */
function checkColorFor(hex: string): string {
  const l = luminance(hex);
  const vsWhite = 1.05 / (l + 0.05);
  const vsDark = (l + 0.05) / (luminance(DARK_CHECK) + 0.05);
  return vsWhite >= vsDark ? LIGHT_CHECK : DARK_CHECK;
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const PREV_KEYS = new Set(['ArrowLeft', 'ArrowUp']);

/** 24-colour radio group with roving focus and arrow-key navigation (§10.6). */
export function SwatchPicker({ value, onChange, usedColors = [], label = 'Colour' }: SwatchPickerProps): JSX.Element {
  const selected = normalizeHex(value);
  const used = new Set(usedColors.map(normalizeHex));
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = Math.max(
    0,
    PROJECT_PALETTE.findIndex((c) => c.hex === selected),
  );

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = NEXT_KEYS.has(event.key) ? 1 : PREV_KEYS.has(event.key) ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const nextIndex = (index + step + PROJECT_PALETTE.length) % PROJECT_PALETTE.length;
    const next = PROJECT_PALETTE[nextIndex];
    if (!next) return;
    onChange(next.hex);
    buttons.current[nextIndex]?.focus();
  }

  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-6 gap-2 sm:grid-cols-12">
      {PROJECT_PALETTE.map((color, index) => {
        const isSelected = color.hex === selected;
        const isUsed = used.has(color.hex) && !isSelected;
        return (
          <button
            key={color.hex}
            ref={(el) => {
              buttons.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={`${color.name}, ${color.hex}`}
            title={isUsed ? `${color.name} (used by another project)` : color.name}
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => onChange(color.hex)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'relative flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-border transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              isSelected && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
            )}
            style={{ backgroundColor: color.hex }}
          >
            {isSelected && <Check className="h-4 w-4" style={{ color: checkColorFor(color.hex) }} aria-hidden="true" />}
            {isUsed && (
              <span
                aria-hidden="true"
                className="absolute h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: checkColorFor(color.hex) }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
